import { useState, useCallback } from 'react';
import { useAccount, usePublicClient, useWalletClient, useNetwork } from 'wagmi';
import { parseEther, parseUnits, formatUnits } from 'viem';
import { useToast } from '@/components/ui/use-toast';

// Multisender contract ABI (simplified)
const MULTISENDER_ABI = [
  {
    inputs: [
      { name: 'recipients', type: 'address[]' },
      { name: 'amounts', type: 'uint256[]' }
    ],
    name: 'multiSendNative',
    outputs: [],
    stateMutability: 'payable',
    type: 'function'
  },
  {
    inputs: [
      { name: 'token', type: 'address' },
      { name: 'recipients', type: 'address[]' },
      { name: 'amounts', type: 'uint256[]' }
    ],
    name: 'multiSendToken',
    outputs: [],
    stateMutability: 'payable',
    type: 'function'
  },
  {
    inputs: [{ name: 'recipientCount', type: 'uint256' }],
    name: 'getFeeForRecipients',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'feePerRecipient',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  }
] as const;

// ERC20 ABI for token operations
const ERC20_ABI = [
  {
    inputs: [{ name: 'owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'symbol',
    outputs: [{ name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function'
  }
] as const;

// Multisender contract addresses by chain ID
const MULTISENDER_ADDRESSES: { [key: number]: string } = {
  97: '0x280C472DdeeAEeB7d68D4225f53d99AFe92ffcdb', // BSC Testnet - MdbAppBscTestMLTSND
  56: '0xDdD20E3A77F8AC57C944071B7e7558cB93D97BE2', // BSC Mainnet - MdbAppBscMLTSND
  5611: '0xD5A01db5D8f7786d62A1D066849181F5bFF6dF55', // opBNB Testnet - MdbAppopBNBTestMLTSND
  204: '0xA55C79b67f91D86050950E288083F776fBbf274a', // opBNB Mainnet - MdbAppopBNBMLTSND
  43113: '0xdD3e3F489287210edC86c6b960B8Fb651886A5c0', // Avalanche Fuji Testnet - MdbAppAvaxTestMLTSND
  43114: '0x1C365844B733f4Cb429CDB944De135179B3d5cD7', // Avalanche Mainnet - MdbAppAvaxMLTSND
  11155111: '0x93D40CA2EEe10d7f36862f13D6F7E27E3B5CbA08', // Ethereum Sepolia - MdbAppSepoliaMLTSND
  11124: '0xc2E52f948De3242E40950f8DB4Acb2eECd82a98C', // Abstract Testnet - MdbAppAbstractTestMLTSND
  2741: '0xA55C79b67f91D86050950E288083F776fBbf274a', // Abstract Mainnet - MdbAppAbstractMLTSND
  421614: '0xD29E72179bAB2880348124ed958024353ae2Ad92', // Arbitrum Sepolia - MdbAppArbSepoliaMLTSND
  42161: '0x5d999590256ADBE2B80D0B74b757FFfde6B940ca', // Arbitrum One - MdbAppArbMLTSND
  11155420: '0xA55C79b67f91D86050950E288083F776fBbf274a', // Optimism Sepolia - MdbAppOPTSepoliaMLTSND
  10: '0xB1019Ff4c295d66443F3cbB48439d2239e0eD3bf', // Optimism - MdbAppOPTMLTSND
  84532: '0x4FF205ef97ee528c8FB4bB36bA67Fd8a95e8b3f0', // Base Sepolia - MdbAppBASESepoliaMLTSND
  8453: '0xA55C79b67f91D86050950E288083F776fBbf274a', // Base - MdbAppBASEMLTSND
  1: '0x47d6A0B2a8E65ff135682A517372bEEf53607aF1',  // Ethereum Mainnet - MdbAppETHMLTSND
  80002: '0xA55C79b67f91D86050950E288083F776fBbf274a', // Polygon Amoy - MdbAppPolyAmoyMLTSND
  137: '0xB1019Ff4c295d66443F3cbB48439d2239e0eD3bf', // Polygon Mainnet - MdbAppPolyMaticMLTSND
  1328: '0xA55C79b67f91D86050950E288083F776fBbf274a', // Sei Atlantic-2 Testnet - MdbAppSeiTestMLTSND
  1329: '0xA55C79b67f91D86050950E288083F776fBbf274a', // Sei Mainnet - MdbAppSeiMLTSND
};

interface Recipient {
  address: string;
  amount: string;
}

export function useMultisender() {
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  // Get token decimals
  const getTokenDecimals = useCallback(async (tokenAddress: string): Promise<number> => {
    if (!publicClient) return 18;

    try {
      const decimals = await publicClient.readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'decimals',
      });
      return Number(decimals);
    } catch (error) {
      console.error('Failed to read token decimals:', error);
      return 18;
    }
  }, [publicClient]);

  // Get token balance for an owner
  const getTokenBalance = useCallback(async (tokenAddress: string, owner: string): Promise<bigint> => {
    if (!publicClient) return BigInt(0);

    try {
      const balance = await publicClient.readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [owner as `0x${string}`],
      });
      return balance as bigint;
    } catch (error) {
      console.error('Failed to read token balance:', error);
      return BigInt(0);
    }
  }, [publicClient]);

  // Get platform fee for a given number of recipients
  const getPlatformFee = useCallback(async (recipientCount: number, chainId: number): Promise<bigint> => {
    if (!publicClient) return BigInt(0);

    const multisenderAddress = MULTISENDER_ADDRESSES[chainId];
    if (!multisenderAddress || multisenderAddress === '0x0000000000000000000000000000000000000000') {
      return BigInt(0);
    }

    try {
      const fee = await publicClient.readContract({
        address: multisenderAddress as `0x${string}`,
        abi: MULTISENDER_ABI,
        functionName: 'getFeeForRecipients',
        args: [BigInt(recipientCount)],
      });
      return fee as bigint;
    } catch (error) {
      console.error('Failed to read platform fee:', error);
      // Fallback: 0.0025 BNB per recipient
      return parseEther('0.0025') * BigInt(recipientCount);
    }
  }, [publicClient]);

  // Send native tokens (BNB, ETH, etc.)
  const sendNative = useCallback(async (
    recipients: Recipient[],
    chainId: number
  ) => {
    if (!isConnected || !address || !publicClient || !walletClient) {
      toast({
        title: "Error",
        description: "Please connect your wallet",
        variant: "destructive",
      });
      return;
    }

    const multisenderAddress = MULTISENDER_ADDRESSES[chainId];
    if (!multisenderAddress || multisenderAddress === '0x0000000000000000000000000000000000000000') {
      toast({
        title: "Error",
        description: "Multisender not deployed on this network yet",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const addresses = recipients.map(r => r.address as `0x${string}`);
      const amounts = recipients.map(r => parseEther(r.amount));
      const totalAmount = amounts.reduce((sum, amt) => sum + amt, BigInt(0));
      const platformFee = await getPlatformFee(recipients.length, chainId);
      const totalValue = totalAmount + platformFee;

      // Check wallet balance
      const balance = await publicClient.getBalance({ address: address as `0x${string}` });
      if (balance < totalValue) {
        toast({
          title: "Insufficient balance",
          description: `You need ${formatUnits(totalValue, 18)} ${chain?.nativeCurrency?.symbol || 'BNB'} but only have ${formatUnits(balance, 18)}`,
          variant: "destructive",
        });
        return false;
      }

      // Send transaction
      const hash = await walletClient.writeContract({
        address: multisenderAddress as `0x${string}`,
        abi: MULTISENDER_ABI,
        functionName: 'multiSendNative',
        args: [addresses, amounts],
        value: totalValue,
      });

      setTxHash(hash);
      
      toast({
        title: "Transaction submitted!",
        description: `Sending to ${recipients.length} recipients. Waiting for confirmation...`,
      });

      // Wait for transaction
      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (receipt.status === 'success') {
        toast({
          title: "Success!",
          description: `Sent ${formatUnits(totalAmount, 18)} ${chain?.nativeCurrency?.symbol || 'BNB'} to ${recipients.length} recipients`,
        });
        return true;
      } else {
        toast({
          title: "Transaction failed",
          description: "The transaction was reverted",
          variant: "destructive",
        });
        return false;
      }
    } catch (error: any) {
      console.error('Send native error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send tokens",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSending(false);
    }
  }, [isConnected, address, toast, publicClient, walletClient, chain, getPlatformFee]);

  // Send ERC20 tokens
  const sendToken = useCallback(async (
    tokenAddress: string,
    recipients: Recipient[],
    chainId: number
  ) => {
    if (!isConnected || !address || !publicClient || !walletClient) {
      toast({
        title: "Error",
        description: "Please connect your wallet",
        variant: "destructive",
      });
      return;
    }

    const multisenderAddress = MULTISENDER_ADDRESSES[chainId];
    if (!multisenderAddress || multisenderAddress === '0x0000000000000000000000000000000000000000') {
      toast({
        title: "Error",
        description: "Multisender not deployed on this network yet",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const decimals = await getTokenDecimals(tokenAddress);
      const addresses = recipients.map(r => r.address as `0x${string}`);
      const amounts = recipients.map(r => parseUnits(r.amount, decimals));
      const totalAmount = amounts.reduce((sum, amt) => sum + amt, BigInt(0));
      const platformFee = await getPlatformFee(recipients.length, chainId);

      // Check token balance
      const balance = await getTokenBalance(tokenAddress, address as string);
      if (balance < totalAmount) {
        toast({
          title: "Insufficient token balance",
          description: `You need ${formatUnits(totalAmount, decimals)} tokens but only have ${formatUnits(balance, decimals)}`,
          variant: "destructive",
        });
        return false;
      }

      // Check BNB balance for platform fee
      const bnbBalance = await publicClient.getBalance({ address: address as `0x${string}` });
      if (bnbBalance < platformFee) {
        toast({
          title: "Insufficient BNB for fee",
          description: `You need ${formatUnits(platformFee, 18)} BNB for the platform fee`,
          variant: "destructive",
        });
        return false;
      }

      // Step 1: Check allowance
      const allowance = await publicClient.readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address as `0x${string}`, multisenderAddress as `0x${string}`],
      }) as bigint;

      // Step 2: Approve if needed
      if (allowance < totalAmount) {
        toast({
          title: "Approval required",
          description: `Approving ${formatUnits(totalAmount, decimals)} tokens...`,
        });

        const approveHash = await walletClient.writeContract({
          address: tokenAddress as `0x${string}`,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [multisenderAddress as `0x${string}`, totalAmount],
        });

        await publicClient.waitForTransactionReceipt({ hash: approveHash });

        toast({
          title: "Approved!",
          description: "Now sending tokens...",
        });
      }

      // Step 3: Execute multisend
      const hash = await walletClient.writeContract({
        address: multisenderAddress as `0x${string}`,
        abi: MULTISENDER_ABI,
        functionName: 'multiSendToken',
        args: [tokenAddress as `0x${string}`, addresses, amounts],
        value: platformFee,
      });

      setTxHash(hash);
      
      toast({
        title: "Transaction submitted!",
        description: `Sending to ${recipients.length} recipients. Waiting for confirmation...`,
      });

      // Wait for transaction
      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (receipt.status === 'success') {
        toast({
          title: "Success!",
          description: `Sent ${formatUnits(totalAmount, decimals)} tokens to ${recipients.length} recipients`,
        });
        return true;
      } else {
        toast({
          title: "Transaction failed",
          description: "The transaction was reverted",
          variant: "destructive",
        });
        return false;
      }
    } catch (error: any) {
      console.error('Send token error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send tokens",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSending(false);
    }
  }, [isConnected, address, toast, publicClient, walletClient, getTokenDecimals, getTokenBalance, getPlatformFee]);

  return {
    sendNative,
    sendToken,
    isSending,
    txHash,
    isConnected,
    getTokenDecimals,
    getTokenBalance,
    getPlatformFee,
  };
}

export { MULTISENDER_ABI, ERC20_ABI, MULTISENDER_ADDRESSES };
